import { useCallback, useMemo } from 'react';

import type {
  CellValueChangedEvent,
  GridApi,
  GridReadyEvent,
  RowStyle,
} from 'ag-grid-community';
import { AgGridReact, type AgGridReactProps } from 'ag-grid-react';

import { useDefaultGridOptions } from '@/grid';
import type { ColDef } from '@/grid/types';

import type { BulkDonorScheduleState } from '../hooks/use-bulk-donor-schedule-store';
import { CellRenderer } from './cell-renderer';

type GridState<Row> = Pick<
  BulkDonorScheduleState<Row>,
  'errors' | 'messages' | 'readonly' | 'rows' | 'type' | 'warningRowIndexes'
>;

type BulkDonorScheduleGridProps<Row> = {
  columns: ColDef<Row>[];
  onEditingChange: (editing: boolean) => void;
  onReady: (api: GridApi<Row>) => void;
  onRowChanged: (rowIndex: number, row: Row, field: string) => void;
  state: GridState<Row>;
};

export function BulkDonorScheduleGrid<Row>({
  columns,
  onEditingChange,
  onReady,
  onRowChanged,
  state,
}: BulkDonorScheduleGridProps<Row>) {
  const defaultGridOptions = useDefaultGridOptions();

  const errorRowIndexes = useMemo(
    () =>
      new Set(
        Object.keys(state.errors ?? {}).map((errorKey) =>
          Number(errorKey.split('.')[0]),
        ),
      ),
    [state.errors],
  );

  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent<Row>) => {
      const rowIndex = event.rowIndex;
      const data = event.data;
      const field = event.colDef.field;
      if (rowIndex === null || data === undefined || !field) {
        return;
      }

      const focusedCell = event.api.getFocusedCell();
      onRowChanged(rowIndex, data, field);

      if (focusedCell) {
        event.api.stopEditing();

        setTimeout(() => {
          event.api.setFocusedCell(
            focusedCell.rowIndex,
            focusedCell.column,
            focusedCell.rowPinned,
          );
          event.api.startEditingCell({
            rowIndex: focusedCell.rowIndex,
            colKey: focusedCell.column,
            rowPinned: focusedCell.rowPinned,
          });
        }, 300);
      }
    },
    [onRowChanged],
  );

  const handleGridReady = useCallback(
    (event: GridReadyEvent<Row>) => onReady(event.api),
    [onReady],
  );

  const defaultColDef = useMemo<AgGridReactProps['defaultColDef']>(
    () => ({
      cellRenderer: CellRenderer,
      editable: !state.readonly,
      resizable: true,
      suppressHeaderMenuButton: true,
      tooltipValueGetter(params) {
        const rowIndex = params.rowIndex;
        const field =
          params.colDef && 'field' in params.colDef
            ? params.colDef.field
            : undefined;
        if (rowIndex === undefined || !field) {
          return;
        }

        const error = state.errors?.[`${rowIndex}.${field}`];
        const message = state.messages?.[`${rowIndex}.${field}`]?.message;

        return error || message;
      },
      cellStyle: (params) => {
        const baseStyle: Record<string, string> = {
          borderRight: '1px solid var(--ag-border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.25rem',
        };

        if (state.type === 'success') {
          const isWarningRow =
            params.rowIndex !== null &&
            state.warningRowIndexes.has(params.rowIndex);
          return {
            ...baseStyle,
            borderRightColor: isWarningRow
              ? 'var(--color-amber-500)'
              : 'var(--color-emerald-500)',
          };
        }

        return baseStyle;
      },
    }),
    [
      state.errors,
      state.messages,
      state.readonly,
      state.type,
      state.warningRowIndexes,
    ],
  );

  const getRowStyle = useCallback(
    (
      params: Parameters<NonNullable<AgGridReactProps['getRowStyle']>>[0],
    ): RowStyle | undefined => {
      if (
        params.node.rowIndex !== null &&
        errorRowIndexes.has(params.node.rowIndex)
      ) {
        return {
          borderColor: 'var(--color-red-500)',
          backgroundColor:
            'color-mix(in srgb, var(--ag-background-color), var(--color-red-400) 28%)',
        };
      }
      if (
        state.type === 'success' &&
        params.node.rowIndex !== null &&
        state.warningRowIndexes.has(params.node.rowIndex)
      ) {
        return {
          borderColor: 'var(--color-amber-500)',
          backgroundColor:
            'color-mix(in srgb, var(--ag-background-color), var(--color-amber-400) 28%)',
        };
      }
      if (state.type === 'success') {
        return {
          borderColor: 'var(--color-emerald-500)',
          backgroundColor:
            'color-mix(in srgb, var(--ag-background-color), var(--ag-foreground-color) 10%)',
        };
      }
      if (state.readonly) {
        return {
          borderColor: '',
          backgroundColor:
            'color-mix(in srgb, var(--ag-background-color), var(--ag-foreground-color) 10%)',
        };
      }
    },
    [errorRowIndexes, state.readonly, state.type, state.warningRowIndexes],
  );

  return (
    <div className="h-full w-full overflow-auto">
      <AgGridReact
        gridOptions={defaultGridOptions}
        className="ag-theme-transova h-full [--ag-border-radius:0]"
        suppressCsvExport
        suppressExcelExport
        suppressHorizontalScroll={false}
        tooltipInteraction
        tooltipShowDelay={0}
        context={state}
        defaultColDef={defaultColDef}
        columnDefs={columns as AgGridReactProps['columnDefs']}
        onCellValueChanged={handleCellValueChanged}
        onGridReady={handleGridReady}
        rowData={state.rows}
        getRowStyle={getRowStyle}
        enableRangeSelection
        enableFillHandle
        autoSizePadding={2}
        stopEditingWhenCellsLoseFocus
        onCellEditingStarted={() => onEditingChange(true)}
        onCellEditingStopped={() => onEditingChange(false)}
      />
    </div>
  );
}
